import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { EditorInputField, Section } from '../../editor-container/editor-model';
import { ContentElement, ContentStatusEnum, GridCell, HeadFootEnum, RowCell } from '@urbannex/storethemes';
import { Subject, Subscription } from 'rxjs';
import { CellEditorStoreService, LastActionEnum } from '../cell-editor-store.service';
import { CssServicesService } from '../../services/css-services.service';
import { take, takeUntil } from 'rxjs/operators';
import { NbDialogService, NbPopoverDirective } from '@nebular/theme';
import { ColorPickerPopoverComponent } from '../dialogs/color-picker-popover/color-picker-popover.component';
import { BorderPresetPopoverComponent } from '../dialogs/border-preset-popover/border-preset-popover.component';
import { PaddingPresetPopoverComponent } from '../dialogs/padding-preset-popover/padding-preset-popover.component';
import { SampleImageDirectoryComponent } from '../sample-image-directory/sample-image-directory.component';
import { SliderPopoverComponent } from '../dialogs/slider-popover/slider-popover.component';
import { FontSizePopoverComponent } from '../dialogs/font-size-popover/font-size-popover.component';
import { FontWeightPopoverComponent } from '../dialogs/font-weight-popover/font-weight-popover.component';
import { FontFamilyPopoverComponent } from '../dialogs/font-family-popover/font-family-popover.component';
import { SectionBuilderStoreService } from '../sections-editor/shared-section-builder/section-builder-store.service';
import { UnderlinePresetPopoverComponent } from '../dialogs/underline-preset-popover/underline-preset-popover.component';

@Component({
  selector: 'kve-cell-theme-editor',
  templateUrl: './cell-theme-editor.component.html',
  styleUrls: ['./cell-theme-editor.component.scss']
})
export class CellThemeEditorComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() section: Section
  @Input() cellId: string
  @Input() tabbed: boolean
  @Input() status: ContentStatusEnum;
  contentElements: ContentElement[];

  cellOptions: GridCell | RowCell;

  layoutClass: string;
  headerFooterOption: HeadFootEnum;
  rendererType: string;

  themeRoot: HTMLElement;

  selectedCell: GridCell;
  selectedCell$: Subscription;

  themeClassList = [
    "cell-theme-default",
    "cell-theme-primary",
    "cell-theme-info",
    "cell-theme-success",
    "cell-theme-danger"
  ]

  themeClass$: Subscription;
  themeClass: string;
  layoutClass$: Subscription;

  themes = [
    "nb-theme-default",
    "nb-theme-earthtone",
    "nb-theme-green-house",
    "nb-theme-makeup"
  ]

  selectedContent;
  selectedHeader;
  selectedWeight;
  selectedFontSize;
  selectedFontFam;
  selectedAlignment;
  selectedWidth;
  textColor: string;
  selectedDelcaration;

  selectedContainerLayout;

  contents = [
    { name: "Header", selector: "header" },
    { name: "Text", selector: "banner-text" },
    { name: "Footer", selector: "footer" }
  ]

  headers = [
    { name: "Header 6", value: "h6" },
    { name: "Header 5", value: "h5" },
    { name: "Header 4", value: "h4" },
    { name: "Header 3", value: "h3" },
    { name: "Header 2", value: "h2" },
    { name: "Header 1", value: "h1" },
  ]

  showPassword = true;

  weights = [
    { name: "Normal", value: 400 },
    { name: "Bold", value: 600 },
    { name: "Bolder", value: 700 }
  ]

  fontSizes = [
    { name: "Tiny", value: "0.625rem", class: "fnt-size-tiny" },
    { name: "Small", value: "0.75rem", class: "fnt-size-small" },
    { name: "Medium Small", value: "0.875rem", class: "fnt-size-medium" },
    { name: "Standard", value: "0.9375rem", class: "fnt-size-standard" },
    { name: "Standard Large", value: "1rem", class: "fnt-size-standard-large" },
    { name: "Standard Larger", value: "1.125rem", class: "fnt-size-standard-larger" },
    { name: "Large", value: "1.375rem", class: "fnt-size-large" },
    { name: "Larger", value: "1.625rem", class: "fnt-size-larger" },
    { name: "Very Large", value: "1.875rem", class: "fnt-size-very-large" },
    { name: "Giant", value: "2rem", class: "fnt-size-gigantic" },
    { name: "Enormous", value: "2.25rem", class: "fnt-size-enormous" }
  ]

  widthOptions = [
    { name: "Full Width", value: "kve-fullwidth" },
    { name: "auto", value: "kve-autoWidth" },
  ]

  fontFams = [
    {
      name: "Arial",
      value: "Arial, sans-serif"
    },
    {
      name: "Courier New",
      value: "Courier New, monospace"
    },
    {
      name: "Georgia",
      value: "Georgia, serif"
    },
    {
      name: "Lato",
      value: "Lato, sans-serif"
    },
    {
      name: "Lobster",
      value: "Lobster, sans-serif"
    },
    {
      name: "Merriweather",
      value: "Merriweather, sans-serif"
    },
    {
      name: "Montserrat",
      value: "Montserrat, sans-serif"
    },
    {
      name: "Open Sans",
      value: "Open Sans, sans-serif"
    },
    {
      name: "Oswald",
      value: "Oswald, sans-serif"
    },
    {
      name: "PT Sans",
      value: "PT Sans, sans-serif"
    },
    {
      name: "Playfair Display",
      value: "Playfair Display,serif"
    },
    {
      name: "Raleway",
      value: "Raleway, sans-serif"
    },
    {
      name: "Roboto",
      value: "Robotto, sans-serif"
    },
    {
      name: "Roboto Condensed",
      value: "Roboto Condensed, sans-serif"
    },
    {
      name: "Roboto Slab",
      value: "Roboto Slab, sans-serif"
    },
    {
      name: "Source Sans 3",
      value: "Source Sans 3, sans-serif"
    },
    {
      name: "Ubuntu",
      value: "Ubuntu, sans-serif"
    }
  ]

  containerLayouts = [
    { name: "Content Top", class: "container-content-top" },
    { name: "Content Middle", class: "container-content-middle" },
    { name: "Content Bottom", class: "container-content-bottom" }
  ]

  paddingField: EditorInputField = {
    name: 'padding',
    title: 'Padding',
    type: 'padding',
    controlType: 'multiMeasure'
  }
  marginField: EditorInputField = {
    name: "margin",
    title: "Margin",
    type: "margin",
    controlType: "multiMeasure"
  }
  borderField: EditorInputField = {
    name: 'border',
    title: "Border",
    type: "border",
    controlType: "multiMeasure"
  }

  alignments = [
    { name: "Align Left", class: "justify-left" },
    { name: "Align Center", class: "justify-center" },
    { name: "Align Right", class: "justify-right" }
  ]

  layoutDeclarations = [
    { name: "Padding", declaration: "padding" },
    { name: "Margin", declaration: "margin" },
    { name: "Border", declaration: "border" },
    { name: "Width", declaration: "width" }
  ]

  bgColors$: Subscription;
  pmColors$: Subscription;
  activeSelector$: Subscription;
  bgBasic: string[] = []
  bgPrimary: string[] = []
  bgAlt: string[] = []
  fillColors: string[];
  primaryColors: string[] = [];

  headerTxTClr$: Subscription;
  footerTxtClr$: Subscription;
  textClr$: Subscription;

  headerTextColor: string;
  footerTextColor: string;

  destroy$ = new Subject<void>();
  valueAll = 0;

  component: any = ColorPickerPopoverComponent;

  borderPop: any = BorderPresetPopoverComponent;

  paddingPop: any = PaddingPresetPopoverComponent;

  fontSizePop: any = FontSizePopoverComponent;
  fontWeightPop: any = FontWeightPopoverComponent;
  fontFamPop: any = FontFamilyPopoverComponent;

  underlinePop: any = UnderlinePresetPopoverComponent;



  @ViewChild('list', { read: TemplateRef }) templateList: TemplateRef<any>;
  @ViewChild(NbPopoverDirective) popover: NbPopoverDirective;

  constructor(
    protected cellEditorStoreService: CellEditorStoreService,
    protected sectionBuilderStoreService: SectionBuilderStoreService,
    private cssService: CssServicesService,
    private rootElement: ElementRef,
    private dialogService: NbDialogService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["status"]) {
      if (this.status != undefined) {

        console.log(this.status);
        console.log(this.section);
        if (this.section?.status !== undefined) {
          if (this.section.status === "Initial Draft") {
            console.log("It's an Initial Draft")
            this.status = this.section.status;
            this.selectedCell$ = this.sectionBuilderStoreService.selectedCell$.subscribe((selectedCell: GridCell) => {
              console.log(selectedCell);
              if (selectedCell != null && selectedCell != undefined) {
                this.selectedCell = selectedCell;
                this.contentElements = this.setContentElements(this.selectedCell);
              }
            })
            this.activeSelector$ = this.sectionBuilderStoreService.selectedContent$.subscribe((selectedContent: any) => {
              console.log(selectedContent)
              this.selectedContent = selectedContent ? selectedContent : this.contentElements[0];
            })
          }

        }
      }
    }
    /* if(changes["trackStatus$"]){
      this.trackStatus$.subscribe((status) => {
        console.log(status)
      })
    } */
    /* this.trackStatus$.subscribe((status) => {

      if(this.status != undefined){
        if (this.status !== this.status) {
          console.log(this.status);
          console.log(this.section);
          if(this.section?.status !== undefined){
            if(this.section.status === "Initial Draft"){
              console.log("It's an Initial Draft")
              this.status = this.section.status;
              this.selectedCell$ = this.sectionBuilderStoreService.selectedCell$.subscribe((selectedCell:GridCell) =>{
                console.log(selectedCell);
                if(selectedCell != null && selectedCell != undefined){
                  this.selectedCell = selectedCell;
                  this.contentElements = this.setContentElements(this.selectedCell);
                }
              })
              this.activeSelector$ = this.sectionBuilderStoreService.selectedContent$.subscribe((selectedContent:any) => {
                console.log(selectedContent)
                this.selectedContent = selectedContent?selectedContent:this.contentElements[0];
              })
            }
          }
        }
      }
    }) */
    if (changes["selectedCell"]) {
      console.log(this.section);
    }
    if (changes["section"]) {
      if (this.section != undefined) {
        console.log(this.section);
        this.rendererType = this.section.rendererType;
        if (this.section.status != undefined) {
          if (this.section.status === "Initial Draft") {
            console.log("It's an Initial Draft")
            this.status = this.section.status;
            this.selectedCell$ = this.sectionBuilderStoreService.selectedCell$.subscribe((selectedCell: GridCell) => {
              console.log(selectedCell);
              if (selectedCell != null && selectedCell != undefined) {
                this.selectedCell = selectedCell;
                this.contentElements = this.setContentElements(this.selectedCell);
              }
            })
            this.activeSelector$ = this.sectionBuilderStoreService.selectedContent$.subscribe((selectedContent: any) => {
              console.log(selectedContent)
              this.selectedContent = selectedContent ? selectedContent : this.contentElements[0];
            })
          } else {
            console.log("Not resolving Initial Draft")
          }
        } else {
          console.log("status is undefined for " + this.section.status);
          this.activeSelector$ = this.cellEditorStoreService.activeSelector$.subscribe((selectedContent: any) => {
            console.log(selectedContent)
            this.selectedContent = selectedContent;
          })
          this.selectedCell$ = this.cellEditorStoreService.selectedCell$.subscribe((selectedCell: GridCell) => {
            console.log(selectedCell);
            if (selectedCell != null && selectedCell != undefined) {
              this.selectedCell = selectedCell;
              this.contentElements = this.setContentElements(this.selectedCell);
              if (!this.selectedContent) {
                this.selectedContent = this.contentElements[0];
              }
            }
          })

        }
      }

    }
  }


  ngOnInit(): void {



    this.selectedHeader = this.headers[5];
    this.selectedWeight = this.weights[0];
    this.selectedFontSize = this.fontSizes[3];
    this.selectedDelcaration = this.layoutDeclarations[0];
    this.textColor = "var(--text-basic-color)"

    /* this.selectedCell$ = this.cellEditorStoreService.selectedCell$.subscribe((selectedCell:GridCell) => {
      if(selectedCell != null && selectedCell != undefined){
        this.selectedCell = selectedCell;
        this.contentElements = this.selectedCell.contentElements;
        if(this.contentElements != undefined){
          this.selectedContent = this.contentElements[0];
          console.log(this.selectedContent);
        }
        this.cellEditorStoreService.updateActiveSelector(this.selectedContent)
        if(selectedCell.headerFooterEnum){
          this.headerFooterOption = selectedCell.headerFooterEnum
        }else{
          this.headerFooterOption = HeadFootEnum.BOTH;
        }
      }
    }) */

    this.themeClass$ = this.cellEditorStoreService.theme$.subscribe((_themeClass: string) => {
      if (_themeClass != null && _themeClass != undefined) {
        this.themeClass = _themeClass;
      } else {
        this.themeClass = "cell-theme-default"
      }
    })
    this.layoutClass$ = this.cellEditorStoreService.layoutClass$.subscribe((_layoutClass: string) => {
      if (_layoutClass != null && _layoutClass != undefined) {
        this.layoutClass = _layoutClass;
      } else {
        this.layoutClass = "cell-layout-default"
      }
    })

    this.bgColors$ = this.cssService.getBgColors().pipe(take(1)).subscribe((colorKV: any[]) => {
      // console.log(colorKV);
      colorKV.forEach((colorObj, i) => {
        if (i < 4) {
          this.bgBasic.push(colorObj.val)
        } else if (i > 3 && i < 8) {
          this.bgPrimary.push(colorObj.val)
        } else if (i > 7) {
          this.bgAlt.push(colorObj.val)
        }

      })
      // this.fillColors = this.bgBasic.concat(this.bgPrimary).concat(this.bgAlt);
    })
    this.pmColors$ = this.cssService.getPmColors().pipe(take(1))
      .subscribe((colorKV: any[]) => {
        // console.log(colorKV)
        colorKV.forEach((colorObj) => {
          this.primaryColors.push(colorObj.val)
        })
        // console.log(this.primaryColors);
        this.fillColors = this.bgBasic.concat(this.primaryColors).concat(this.bgAlt);
      })
  }

  setContentElements(selectedCell: GridCell | RowCell) {
    return selectedCell.contentElements;
  }

  onUseTheme() {
    this.cellEditorStoreService.updateLastActionState(LastActionEnum.THEME_SET);
    console.log("cell-editor-store.service.ts - updating selected cell theme: " + this.selectedCell);
    this.cellEditorStoreService.updateSelectedCell(this.selectedCell);
  }

  onSelectTheme(themeClass) {
    this.cellEditorStoreService.updateTheme(themeClass);
    this.selectedCell.themeClass = themeClass;

  }

  getColors(root: HTMLElement) {
    console.log(root);
    const previewHost = <HTMLElement>root.querySelector('.preview-wrapper');
    if (previewHost != undefined) {
      const header = <HTMLElement>previewHost.querySelector('.header');
      if (header != undefined) {
        header.style.getPropertyValue('çolor');
        console.log(header.style.getPropertyValue('çolor'))
      } else {
        console.log("Can find header")
      }

    } else {
      console.log("Cant find previewHost")
    }

    /* this.primaryColors.forEach(col => {
      this.primaryColorsPalette.push(styles.getPropertyValue(col))
    })
    this.successColors.forEach(col => {
      this.successColorsPalette.push(styles.getPropertyValue(col))
    })
    this.infoColors.forEach(col => {
      this.infoColorsPalette.push(styles.getPropertyValue(col))
    })
    this.warningColors.forEach(col => {
      this.warningColorsPalette.push(styles.getPropertyValue(col))
    })
    this.dangerColors.forEach(col => {
      this.dangerColorsPalette.push(styles.getPropertyValue(col))
    })
    this.backgroundsColor.forEach(col => {
      this.backgroundColorsPalette.push(styles.getPropertyValue(col))
    }) */
  }


  ngAfterViewInit(): void {
    console.log(this.contentElements);
    if (this.contentElements != undefined) {
      const themeRoot = <HTMLElement>this.rootElement.nativeElement;
      // console.log(this.themeRoot)
      this.getColors(themeRoot)
    }
  }

  changeContent(e) {

    this.cellEditorStoreService.updateActiveSelector(this.selectedContent)
  }

  getInputType() {
    if (this.showPassword) {
      return 'text';
    }
    return 'password';
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  changeComplete(e) { }
  handleBgChange(e) {
    switch (this.selectedContent.selector) {
      case "header":
        this.cellEditorStoreService.updateHdrContBgClr(e.color.hex)
        break;
      case "footer":
        this.cellEditorStoreService.updateFtrContBgClr(e.color.hex)
        break;
      case "banner-text":
        this.cellEditorStoreService.updateTxtContBgClr(e.color.hex)
        break;

      default:
        break;
    }
  }

  openColorPicker(contentType: string) {
    console.log(this.popover);
    this.popover.content = this.component;
  }

  onChangeFontSize(e) {
    console.log({ content: this.selectedContent, choice: e });
    this.cellEditorStoreService.contentElements$.subscribe((contentElements: ContentElement[]) => {
      console.log(contentElements);
      const contentElement = contentElements.find(element => element.elId == this.selectedContent.elId);
      // ensure only one layout class
      this.fontSizes.forEach(fontSize => {
        if (contentElement.classes.includes(fontSize.class)) {
          const itmIndx = contentElement.classes.indexOf(fontSize.class);
          if (itmIndx !== -1) {
            console.log("Removing classes")
            contentElement.classes.splice(itmIndx, 1);
          }
        }
      })
      contentElement.classes.push(e.class);
      this.cellEditorStoreService.updateContentElements(contentElements);
      this.cellEditorStoreService.updateSelectorPosChange(e.class);
    })
  }

  onChangeWeight(e) {
    console.log(e);
    this.cellEditorStoreService.contentElements$.subscribe((contentElements: ContentElement[]) => {
      const contentElement = contentElements.find(element => element.elId == this.selectedContent.elId);
      contentElement.fontWeight = e.value;
      this.cellEditorStoreService.updateContentElements(contentElements);
    })
  }

  onChangeHeader(e) {
    console.log(e)
  }

  onChangeFontFam(e) {
    console.log(e)
    console.log({ content: this.selectedContent, choice: e });
    this.cellEditorStoreService.contentElements$.subscribe((contentElements: ContentElement[]) => {
      const contentElement = contentElements.find(element => element.elId == this.selectedContent.elId);
      contentElement.fontFamily = e.value;
      this.cellEditorStoreService.updateContentElements(contentElements);
    })
  }

  onChangeContainerLayout(e) {
    console.log(e)
    console.log({ content: this.selectedContent, choice: e });
    this.cellEditorStoreService.contentElements$.subscribe((contentElements: ContentElement[]) => {
      const contentElement = contentElements.find(element => element.elId == this.selectedContent.elId);
      // ensure only one layout class
      this.containerLayouts.forEach(layout => {
        if (contentElement.containerClasses.includes(layout.class)) {
          const itmIndx = contentElement.containerClasses.indexOf(layout.class);
          if (itmIndx !== -1) {
            console.log("Removing classes")
            contentElement.containerClasses.splice(itmIndx, 1);
          }
        }
      })
      contentElement.containerClasses.push(e.class);
      this.cellEditorStoreService.updateContentElements(contentElements);
      this.cellEditorStoreService.updateSelectorPosChange(e.class);
    })
  }

  setContainerLayout(layoutClass: string) {
    this.cellEditorStoreService.contentElements$.subscribe((contentElements: ContentElement[]) => {
      const contentElement = contentElements.find(element => element.elId == this.selectedContent.elId);
      // ensure only one layout class
      this.containerLayouts.forEach(layout => {
        if (contentElement.containerClasses.includes(layout.class)) {
          const itmIndx = contentElement.containerClasses.indexOf(layout.class);
          if (itmIndx !== -1) {
            console.log("Removing classes")
            contentElement.containerClasses.splice(itmIndx, 1);
          }
        }
      })
      contentElement.containerClasses.push(layoutClass);
      this.cellEditorStoreService.updateContentElements(contentElements);
      this.cellEditorStoreService.updateSelectorPosChange(layoutClass);
    })
  }

  onChangeAlign(e) {
    this.cellEditorStoreService.contentElements$.subscribe((contentElements: ContentElement[]) => {
      const contentElement = contentElements.find(element => element.elId == this.selectedContent.elId);
      contentElement.horizontalAlign = e.class;
      // ensure only one alignment class
      this.alignments.forEach(alignment => {
        if (contentElement.classes.includes(alignment.class)) {
          const itmIndx = contentElement.classes.indexOf(alignment.class);
          if (itmIndx !== -1) {
            contentElement.classes.splice(itmIndx, 1);
          }
        }
      })
      contentElement.classes.push(e.class);
      this.cellEditorStoreService.updateContentElements(contentElements);
      this.cellEditorStoreService.updateSelectorPosChange(e.class);
    })
  }

  setHorizontalAlign(layoutClass) {
    this.cellEditorStoreService.contentElements$.subscribe((contentElements: ContentElement[]) => {
      const contentElement = contentElements.find(element => element.elId == this.selectedContent.elId);
      contentElement.horizontalAlign = layoutClass;
      // ensure only one alignment class
      this.alignments.forEach(alignment => {
        if (contentElement.classes.includes(alignment.class)) {
          const itmIndx = contentElement.classes.indexOf(alignment.class);
          if (itmIndx !== -1) {
            contentElement.classes.splice(itmIndx, 1);
          }
        }
      })
      contentElement.classes.push(layoutClass);
      this.cellEditorStoreService.updateContentElements(contentElements);
      this.cellEditorStoreService.updateSelectorPosChange(layoutClass);
    })
  }

  onWidthChange(e) {
    this.cellEditorStoreService.contentElements$.subscribe((contentElements: ContentElement[]) => {
      const contentElement = contentElements.find(element => element.elId == this.selectedContent.elId);
      this.widthOptions.forEach(option => {
        if (contentElement.classes != undefined && contentElement.classes.includes(option.value)) {
          const itmIndx = contentElement.classes.indexOf(option.value);
          if (itmIndx !== -1) {
            contentElement.classes.splice(itmIndx, 1);
          }
        }
      })
      contentElement.classes.push(e.value);
      this.cellEditorStoreService.updateContentElements(contentElements);
    })
  }

  openRandomDialog() {
    this.dialogService.open(SampleImageDirectoryComponent, {
      context: {
        title: 'This is a title passed to the dialog component',
      },
    });
  }

  changeDeclaration(e) {

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


}

